import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { get } from "lodash";
import PropTypes from "prop-types";
import { Divider, Icon, List } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SidenavCollapse from "layouts/Sidenav/SidenavCollapse";
import SidenavRoot from "layouts/Sidenav/SidenavRoot";
import sidenavLogoLabel from "layouts/Sidenav/styles/sidenav";
import { UIController, setMiniSidenav } from "context";
import { useLocale } from "context/locale.context";
import store from "store";
import config from "config/config";
import { list as listScadaProjects } from "services/apis/scadaProject";
import { PRODUCT_VERSION } from "generated/versionCatalog";

const sectionStyle = { display: "block", textTransform: "uppercase", opacity: 0.62, paddingLeft: 3, marginTop: 1.5, marginBottom: 0.75, marginLeft: 1 };

function Sidenav({ detail, color, brand, brandName, routes, ...rest }) {
  void routes;
  const [controller, dispatch] = UIController();
  const { miniSidenav, transparentSidenav } = controller;
  const { t } = useLocale();
  const { pathname } = useLocation();
  const user = store.get("user") || {};
  const isAdmin = user.role === "admin";
  const [projects, setProjects] = useState([]);
  const [expandedProjectId, setExpandedProjectId] = useState("");
  const [communicationsOpen, setCommunicationsOpen] = useState(pathname.startsWith("/system/communications"));

  const pathProjectId = useMemo(() => {
    const match = pathname.match(/^\/(?:scada-projects|projects)\/(\d+)/);
    return match ? match[1] : "";
  }, [pathname]);

  useEffect(() => {
    let active = true;
    const reload = () => listScadaProjects().then((response) => {
      if (!active) return;
      const items = Array.isArray(response?.items) ? response.items : [];
      setProjects(items);
      const stored = String(store.get(`lastProjectId:${user.id}`) || "");
      const next = pathProjectId || (items.some((item) => String(item.id) === stored) ? stored : String(items[0]?.id || ""));
      setExpandedProjectId(next);
      if (next) store.set(`lastProjectId:${user.id}`, next);
    }).catch(() => active && setProjects([]));
    reload();
    window.addEventListener("scada-projects-changed", reload);
    return () => { active = false; window.removeEventListener("scada-projects-changed", reload); };
  }, [isAdmin, pathProjectId]);

  useEffect(() => {
    const handleResize = () => setMiniSidenav(dispatch, window.innerWidth < 1200);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch]);

  brandName = get(detail, ["brandName"], "") || "uSense";
  brand = get(detail, ["logo", "path"]) ? `${config.API_ENDPOINT}/uploaded/${get(detail, ["logo", "path"])}` : brand;
  const title = (text) => <SoftTypography variant="caption" fontWeight="bold" sx={sectionStyle}>{t(text)}</SoftTypography>;
  const item = (name, icon, route, active = pathname === route, extra = {}) => (
    <NavLink to={route} key={`${name}-${route}`}><SidenavCollapse color={color} name={t(name)} icon={<Icon>{icon}</Icon>} active={active} noCollapse {...extra} /></NavLink>
  );
  const toggleProject = (projectId) => {
    const id = String(projectId);
    if (expandedProjectId === id) {
      setExpandedProjectId("");
      return;
    }
    setExpandedProjectId(id);
    store.set(`lastProjectId:${user.id}`, id);
  };

  return <SidenavRoot {...rest} dispatch="" variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
    <SoftBox pt={2} px={3} textAlign="center">
      <SoftBox component={NavLink} to="/dashboard" display="flex" alignItems="center">
        {brand ? <SoftBox component="img" src={brand} alt="uSense" width="2rem" /> : null}
        <SoftBox width="100%" sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}><SoftTypography variant="button" fontWeight="bold">{brandName}</SoftTypography></SoftBox>
      </SoftBox>
    </SoftBox>
    <Divider />
    <List sx={{ px: 0.5 }}>
      {title("Dashboards")}
      {item("Dashboard", "dashboard", "/dashboard", pathname === "/dashboard")}

      {projects.some((project) => project.enabled) ? title("Project Runtime") : null}
      {projects.filter((project) => project.enabled).map((project) => item(project.name, "play_circle", `/scada-projects/${project.id}/runtime`, pathname.startsWith(`/scada-projects/${project.id}/`) && pathname.endsWith("/runtime")))}

      {title("Project")}
      {projects.length ? projects.map((project) => {
        const projectId = String(project.id);
        const open = expandedProjectId === projectId;
        return <SidenavCollapse
          key={`project-${project.id}`}
          color={color}
          name={project.name}
          icon={<Icon>dns</Icon>}
          active={false}
          open={open}
          onClick={() => toggleProject(project.id)}
        >
          <SoftBox pl={1.5}>
            {(isAdmin || project.canDesign) ? item(
              "Design",
              "design_services",
              `/scada-projects/${project.id}/design`,
              pathname === `/scada-projects/${project.id}/design` ||
                (pathname.startsWith(`/scada-projects/${project.id}/screens/`) && pathname.endsWith("/design"))
            ) : null}
            {(isAdmin || project.canDesign) ? item(
              "Simulation",
              "science",
              `/scada-projects/${project.id}/simulation`,
              pathname === `/scada-projects/${project.id}/simulation` ||
                (pathname.startsWith(`/scada-projects/${project.id}/screens/`) && pathname.endsWith("/simulation"))
            ) : null}
            {item(
              "Data Explorer",
              "manage_search",
              `/scada-projects/${project.id}/data-explorer`,
              pathname === `/scada-projects/${project.id}/data-explorer`
            )}
            {item(
              "Alarm",
              "notifications",
              `/projects/${project.id}/alarm`,
              pathname === `/projects/${project.id}/alarm` ||
                pathname === `/projects/${project.id}/alarm-tags` ||
                pathname === `/projects/${project.id}/alarm-manager`
            )}
            {item("History", "history", `/projects/${project.id}/history`)}
            {item("Records", "receipt_long", `/projects/${project.id}/records`)}
            {item("Runtime Diagnostics", "monitor_heart", `/projects/${project.id}/runtime-diagnostics`)}
          </SoftBox>
        </SidenavCollapse>;
      }) : <SoftTypography variant="caption" color="secondary" px={2}>No project assigned</SoftTypography>}

      {title("System")}
      {isAdmin ? <>
        {item("System Info", "info", "/system/info")}
        <SidenavCollapse
          color={color}
          name={t("Communications")}
          icon={<Icon>settings_input_component</Icon>}
          active={pathname.startsWith("/system/communications") && !communicationsOpen}
          open={communicationsOpen}
          onClick={() => setCommunicationsOpen((open) => !open)}
        >
          <SoftBox pl={1.5}>
            {item("Interfaces", "cable", "/system/communications/interfaces")}
            {item("Connections", "hub", "/system/communications/connections")}
            {item("Protocol", "settings_ethernet", "/system/communications/protocols", pathname.startsWith("/system/communications/protocols"))}
          </SoftBox>
        </SidenavCollapse>
        {item("Tag Groups", "style", "/tag-groups")}
        {item("Alarm Types", "notifications_active", "/alarm-types")}
        {item("Project Manager", "account_tree", "/scada-projects")}
        {item("Maintenance", "build", "/system/maintenance")}
        {item("Date & Time", "schedule", "/system/date-time")}
        {item("Account", "people", "/accounts", pathname.startsWith("/accounts"))}
      </> : item("Account", "person", "/account")}

      {title("Help")}
      {item("Manual", "menu_book", "/manual")}
      {item("About", "info", "/about")}
    </List>
    <SoftBox pt={2} my={2} mx={2} mt="auto"><SoftButton style={{ pointerEvents: "none", textTransform: "none" }} variant="gradient" color={color} fullWidth>uSense v{PRODUCT_VERSION}</SoftButton></SoftBox>
  </SidenavRoot>;
}

Sidenav.defaultProps = { color: "info", brand: "", brandName: "" };
Sidenav.propTypes = { detail: PropTypes.object.isRequired, color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]), brand: PropTypes.string, brandName: PropTypes.string, routes: PropTypes.array };
export default connect(({ setting }) => ({ detail: setting.detailForLayout || setting.detail || {} }))(Sidenav);
